import { useCallback, useEffect, useRef } from 'react';
import { normalizeEmails } from '../../helper/spreadsheetView';

export const AUTO_REVEAL_STORAGE_KEY = 'alphasearch:detailsGrid:autoRevealDecided:v1';

// Columns that are hidden by default but revealed once a loaded row holds a
// value for them (#632, owner ruling option 2). Empty until an enrichment runs.
const REVEAL_WHEN_PRESENT = {
  // A found Find-and-Verify email counts too (FE #750).
  emails: (row) => normalizeEmails(row?.emails).length > 0 || Boolean(row?.foundEmail?.email),
  phone: (row) => String(row?.phone ?? '').trim() !== '',
};

const readDecided = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(AUTO_REVEAL_STORAGE_KEY) || '[]');
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const writeDecided = (decided) => {
  try {
    localStorage.setItem(AUTO_REVEAL_STORAGE_KEY, JSON.stringify(Array.from(decided)));
  } catch {
    // Storage unavailable: the decision still holds for this mount.
  }
};

/**
 * Reveals each REVEAL_WHEN_PRESENT column the first time any loaded row has a
 * value for it. A column is revealed at most once: after that — or after the
 * user toggles it by hand — it is "decided" and never touched again, so the
 * reveal cannot fight a deliberate hide. Returns `markDecided(key)`, which the
 * column toggle must call.
 */
export default function useAutoRevealColumns(rows, setVisibleKeys) {
  const decidedRef = useRef(null);
  if (decidedRef.current === null) decidedRef.current = readDecided();

  const markDecided = useCallback((key) => {
    if (!(key in REVEAL_WHEN_PRESENT) || decidedRef.current.has(key)) return;
    decidedRef.current.add(key);
    writeDecided(decidedRef.current);
  }, []);

  useEffect(() => {
    const list = Array.isArray(rows) ? rows : [];
    const toReveal = Object.keys(REVEAL_WHEN_PRESENT).filter(
      (key) => !decidedRef.current.has(key) && list.some(REVEAL_WHEN_PRESENT[key]),
    );
    if (!toReveal.length) return;
    toReveal.forEach(markDecided);
    setVisibleKeys((prev) => new Set([...prev, ...toReveal]));
  }, [rows, setVisibleKeys, markDecided]);

  return markDecided;
}
